import type { Step } from "react-joyride";

/**
 * Tour identifiers — mỗi trang có một tour riêng.
 */
export type TourId =
  | "home"
  | "iching"
  | "iching-result"
  | "tuvi"
  | "tuvi-board"
  | "tuvi-reading"
  | "battu"
  | "battu-board"
  | "battu-reading"
  | "seduyen"
  | "seduyen-discover"
  | "tonghop"
  | "tonghop-session"
  | "boide-hub"
  | "boide-topic"
  | "boi-live"
  | "boi-live-session";

export type TourStepInteraction = {
  event: "click" | "change" | "input" | "pointerdown";
  selector?: string;
  nextLabel?: string;
  advance?: "auto" | "manual";
  completion?: "next" | "finish";
  minLength?: number;
};

export type TourStep = Step & {
  data?: {
    interaction?: TourStepInteraction;
  };
};

/**
 * Tăng version khi nội dung tour thay đổi đáng kể để user cũ được xem lại.
 */
export const TOUR_VERSION = 1;

const STORAGE_PREFIX = "boide_tour_";

function storageKey(id: TourId): string {
  return `${STORAGE_PREFIX}${id}_v${TOUR_VERSION}`;
}

/**
 * Đã xem tour này chưa (an toàn cho SSR — trả false khi không có window).
 */
export function hasSeenTour(id: TourId): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(storageKey(id)) === "1";
  } catch {
    return true;
  }
}

/**
 * Đánh dấu đã xem xong tour để không tự bật lại.
 */
export function markTourSeen(id: TourId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(id), "1");
  } catch {
    /* ignore quota / privacy errors */
  }
}

/**
 * Tiêu đề tour, hiển thị ở nút "Hướng dẫn".
 */
export const TOUR_LABELS: Record<TourId, string> = {
  home: "Tổng quan trang chủ",
  iching: "Hướng dẫn gieo quẻ Kinh Dịch",
  "iching-result": "Hướng dẫn đọc kết quả quẻ",
  tuvi: "Hướng dẫn nhập thông tin Tử Vi",
  "tuvi-board": "Hướng dẫn xem lá số Tử Vi",
  "tuvi-reading": "Hướng dẫn đọc luận giải Tử Vi",
  battu: "Hướng dẫn nhập thông tin Bát Tự",
  "battu-board": "Hướng dẫn xem lá số Bát Tự",
  "battu-reading": "Hướng dẫn đọc luận giải Bát Tự",
  seduyen: "Hướng dẫn cài đặt Se duyên",
  "seduyen-discover": "Hướng dẫn khám phá hữu duyên",
  tonghop: "Hướng dẫn chọn phiên Bói Đê",
  "tonghop-session": "Hướng dẫn luận giải Bói Đê",
  "boide-hub": "Hướng dẫn trang Bói Đê",
  "boide-topic": "Hướng dẫn bắt đầu một lượt bói",
  "boi-live": "Hướng dẫn thiết lập Bói Live",
  "boi-live-session": "Hướng dẫn trò chuyện Bói Live",
};

function StepBody({ children }: { children: React.ReactNode }) {
  return <div className="text-sm leading-relaxed">{children}</div>;
}

/**
 * Kiểm tra phần tử có đang hiển thị thật sự không (loại bỏ các bản layout bị ẩn
 * bằng CSS như `hidden md:flex`).
 */
function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none";
}

/**
 * Tạo target dạng hàm cho react-joyride: thử lần lượt các selector, trả về phần tử
 * ĐANG HIỂN THỊ đầu tiên. Dùng cho các phần tử có nhiều bản (mobile/desktop) hoặc
 * lặp lại (ví dụ 12 cung). Fallback về match đầu tiên nếu không có bản nào hiển thị.
 */
function pickVisible(...selectors: string[]): () => HTMLElement | null {
  return () => {
    for (const sel of selectors) {
      for (const el of Array.from(document.querySelectorAll(sel))) {
        if (isVisible(el)) return el as HTMLElement;
      }
    }
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el instanceof HTMLElement) return el;
    }
    return null;
  };
}

/**
 * Định nghĩa các bước cho từng tour. Target dùng CSS selector `[data-tour="..."]`
 * đã gắn vào DOM ở các trang tương ứng. Bước chào mừng dùng target `body` + placement center.
 */
export const TOUR_STEPS: Record<TourId, TourStep[]> = {
  home: [
    {
      target: "body",
      placement: "center",
      title: "Chào mừng đến Bói Đê 🪷",
      content: (
        <StepBody>
          Đây là nền tảng huyền học Đông phương: gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự, luận
          giải bằng AI. Để mình dẫn bạn đi một vòng nhanh nhé!
        </StepBody>
      ),
    },
    {
      target: '[data-tour="home-cast-cta"]',
      title: "Gieo quẻ Kinh Dịch",
      content: (
        <StepBody>
          Bắt đầu một quẻ Dịch cho câu hỏi đang trăn trở. Mỗi lần gieo là một cuộc đối thoại với
          thiên cơ.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="home-tuvi-cta"]',
      title: "Lập lá số Tử Vi",
      content: (
        <StepBody>
          Xác lập bản mệnh 12 cung từ ngày giờ sinh, rồi để AI luận giải tường tận về vận mệnh.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="home-battu-cta"]',
      title: "Lập lá số Bát Tự",
      content: (
        <StepBody>
          Lập lá số Bát Tự (Tứ Trụ) từ ngày giờ sinh: Tứ Trụ, Nhật chủ, Thập thần, ngũ hành và đại
          vận — AI luận giải vận mệnh theo dụng thần.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="home-features"]',
      title: "Tìm hiểu ba bộ môn",
      content: (
        <StepBody>
          Chưa quen? Mở các thẻ này để đọc giới thiệu chi tiết về Kinh Dịch, Tử Vi Đẩu Số và Bát Tự
          trước khi thực hành.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="home-daily"]',
      title: "Vận khí hôm nay",
      content: (
        <StepBody>
          Tổng quan nhanh về vận khí, hướng tốt và ngũ hành trong ngày — cập nhật riêng cho bạn.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="tour-help-mobile"]', '[data-tour="tour-help"]'),
      title: "Xem lại bất cứ lúc nào",
      content: (
        <StepBody>
          Cần xem lại hướng dẫn? Bấm nút này trên thanh trên cùng để chạy lại tour của trang hiện
          tại.
        </StepBody>
      ),
    },
  ],

  iching: [
    {
      target: "body",
      placement: "center",
      title: "Nghi thức gieo quẻ Kinh Dịch",
      content: (
        <StepBody>
          Ba bước đơn giản: đặt câu hỏi → chọn thời điểm & vị thầy → tụ tâm gieo quẻ. Cùng xem qua
          nhé.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="iching-question"]',
      title: "Bước 1 — Đặt câu hỏi",
      skipScroll: true,
      content: (
        <StepBody>
          Viết một câu hỏi cụ thể, tập trung vào một sự việc (VD: "Dự án sắp tới có thuận lợi
          không?"). Tránh hỏi chung chung và nhiều câu hỏi cùng lúc
        </StepBody>
      ),
      data: {
        interaction: {
          event: "input",
          selector: "input, textarea",
          nextLabel: "Tiếp tục",
          advance: "manual",
          minLength: 5,
        },
      },
    },
    {
      target: pickVisible('[data-tour="iching-casttime"]'),
      title: "Bước 2 — Thời điểm gieo quẻ",
      skipScroll: true,
      content: (
        <StepBody>
          Chọn giờ động tâm. Mặc định là thời điểm hiện tại — bạn nên chọn đúng giờ mà bạn nghĩ ra
          câu hỏi
        </StepBody>
      ),
      data: {
        interaction: {
          event: "change",
          selector: "input, select, button",
          nextLabel: "Tiếp tục",
        },
      },
    },
    {
      target: pickVisible('[data-tour="iching-diviner"]'),
      title: "Chọn vị thầy luận giải",
      skipScroll: true,
      content: (
        <StepBody>
          Mỗi vị thầy có phong cách luận giải riêng. Chọn người bạn thấy hợp để được giải quẻ theo
          đúng "khẩu vị" của bạn.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="iching-cast"]'),
      title: "Bước 3 — Gieo quẻ",
      skipScroll: true,
      content: (
        <StepBody>
          Ấn giữ Bát Quái (hoặc bấm "Gieo quẻ ngay" trên điện thoại) để tụ khí và thỉnh quẻ. Kết quả
          cùng lời luận giải sẽ hiện ra.
        </StepBody>
      ),
      data: {
        interaction: {
          event: "pointerdown",
          selector: "button",
          nextLabel: "Bấm nút gieo quẻ để xem kết quả",
          completion: "finish",
        },
      },
    },
  ],

  tuvi: [
    {
      target: "body",
      placement: "center",
      title: "Lập lá số Tử Vi Đẩu Số 🪷",
      content: (
        <StepBody>
          Chỉ cần thông tin ngày giờ sinh, hệ thống sẽ an sao và lập lá số 12 cung cho bạn. Xem qua
          cách dùng nhé.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tuvi-form"]',
      title: "Nhập thông tin ngày sinh",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Điền họ tên, ngày sinh (dương lịch), giờ sinh và giới tính. Giờ sinh càng chính xác thì lá
          số càng đúng.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tuvi-submit"]',
      title: "Lập lá số",
      skipScroll: true,
      placement: "top",
      content: <StepBody>Bấm "Lập lá số" để lập lá số tử vi của bạn</StepBody>,
      data: {
        interaction: {
          event: "click",
          selector: "button",
          completion: "finish",
        },
      },
    },
  ],

  "tuvi-board": [
    {
      target: "body",
      placement: "center",
      title: "Lá số Tử Vi của bạn 🪷",
      content: (
        <StepBody>
          Lá số đã được lập thành công! Hãy cùng xem qua cách khai mở luận giải đại cục nhé.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="tuvi-khaimo-mobile"]', '[data-tour="tuvi-khaimo"]'),
      title: "Khai mở lá số",
      skipScroll: true,
      content: (
        <StepBody>
          Bấm <strong>"Khai Mở Lá Số"</strong> ở Thiên Bàn (ô giữa) để AI luận giải đại cục — tổng
          quan vận mệnh của lá số này.
        </StepBody>
      ),
      data: {
        interaction: {
          event: "click",
          selector: "button",
          completion: "finish",
        },
      },
    },
  ],

  "tuvi-reading": [
    {
      target: "body",
      placement: "center",
      title: "Luận giải Tử Vi của bạn 🪷",
      content: (
        <StepBody>
          Luận giải chi tiết đã được khai mở! Hãy cùng xem cách đọc và đàm đạo bản mệnh nhé.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="tuvi-reading-anchor"]', '[data-tour="tuvi-reading"]'),
      title: "Đọc luận giải & đàm đạo",
      placement: "bottom",
      skipScroll: true,
      content: (
        <StepBody>
          Lời luận giải hiện ở khung này. Bạn có thể <strong>chat hỏi thêm</strong> để Cổ Nhân giải
          đáp sâu hơn về luận giải.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="tuvi-cung-nav"]', '[data-cung-cell="true"]'),
      title: "Xem chi tiết từng cung",
      skipScroll: true,
      content: (
        <StepBody>
          {typeof window !== "undefined" && window.innerWidth < 1024 ? (
            <>
              Bấm vào tên cung ở đây (<strong>lướt sang trái/phải</strong> để xem thêm các cung) để
              đọc luận giải chi tiết của từng cung, rồi <strong>đàm đạo</strong> để hỏi sâu hơn về
              riêng cung đó.
            </>
          ) : (
            <>
              Bấm hoặc rê chuột vào từng cung ngay trên lá số để đọc luận giải chi tiết của cung đó,
              rồi <strong>đàm đạo</strong> để hỏi sâu hơn về riêng cung đó.
            </>
          )}
        </StepBody>
      ),
    },
  ],

  "iching-result": [
    {
      target: "body",
      placement: "center",
      title: "Đọc kết quả quẻ Dịch",
      content: (
        <StepBody>
          Quẻ đã gieo xong! Mình hướng dẫn bạn cách đọc kết quả: quẻ gốc, hào động, lời luận giải và
          cách hỏi thêm.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="iching-quegoc"]'),
      placement: "bottom",
      title: "Quẻ gốc & 6 hào",
      skipScroll: true,
      content: (
        <StepBody>
          Đây là <strong>quẻ gốc</strong> (quẻ chính) cho hiện tại, gồm 6 hào xếp từ dưới lên. Bấm
          vào từng hào để xem ý nghĩa riêng.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="iching-haodong"]', '[data-tour="iching-quegoc"]'),
      placement: "bottom",
      title: "Hào động → Quẻ biến",
      skipScroll: true,
      content: (
        <StepBody>
          Hào có dấu <strong>★ / BIẾN</strong> là <strong>hào động</strong> — chính nó làm quẻ gốc
          chuyển thành <strong>quẻ biến</strong>, chỉ chiều hướng tương lai.
        </StepBody>
      ),
    },
    {
      target: pickVisible(
        '[data-tour="iching-luangiai-summary"]',
        '[data-tour="iching-luangiai-header"]',
      ),
      placement: "bottom",
      title: "Lời luận giải",
      skipScroll: true,
      content: (
        <StepBody>
          Phần luận giải chi tiết của AI nằm ở đây: lời quẻ, tượng quẻ, ý nghĩa hào động và lời
          khuyên cụ thể cho câu hỏi của bạn.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="iching-chat"]'),
      title: "Hỏi thêm về quẻ",
      skipScroll: true,
      content: (
        <StepBody>
          Còn băn khoăn? Gõ câu hỏi vào đây để <strong>đàm đạo</strong> sâu hơn với thầy về quẻ vừa
          gieo.
        </StepBody>
      ),
    },
  ],
  battu: [
    {
      target: "body",
      placement: "center",
      title: "Lập lá số Bát Tự (Tứ Trụ) 🪷",
      content: (
        <StepBody>
          Chỉ cần họ tên, ngày giờ sinh, hệ thống sẽ lập lá số Bát Tự và luận giải Dụng thần, Đại
          vận cùng các khía cạnh cuộc đời. Hãy xem qua cách dùng nhé!
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-form"]',
      title: "Nhập thông tin bản mệnh",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Nhập họ tên, ngày giờ sinh dương lịch hoặc âm lịch và giới tính. Giờ sinh chính xác giúp
          xác định đúng các trụ.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-submit"]',
      title: "Lập lá số",
      skipScroll: true,
      placement: "top",
      content: <StepBody>Bấm "Lập lá số" để bắt đầu khởi tạo lá số Bát Tự của bạn.</StepBody>,
      data: {
        interaction: {
          event: "click",
          selector: "button",
          completion: "finish",
        },
      },
    },
  ],
  "battu-board": [
    {
      target: "body",
      placement: "center",
      title: "Lá số Bát Tự của bạn 🪷",
      content: (
        <StepBody>
          Lá số đã được thiết lập thành công! Hãy cùng xem qua các thành phần trên lá số và cách
          khai mở luận giải nhé.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-board"]',
      title: "Khung Bản Mệnh",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Lá số gồm bốn trụ (Năm, Tháng, Ngày, Giờ) cùng Thiên Can, Địa Chi, Thập Thần và Nhật Chủ
          đại diện cho bản thân.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-nguhanh"]',
      title: "Cán cân ngũ hành",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Biểu đồ tỷ lệ ngũ hành (Kim, Mộc, Thủy, Hỏa, Thổ) trong lá số. Ngũ hành thừa hay thiếu là
          cơ sở cốt lõi để chọn Dụng thần cải vận.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-diachi"]',
      title: "Hợp - Xung Địa Chi",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Các quan hệ hợp, xung, hình, hại... giữa các địa chi trong lá số của bạn, giúp dự báo các
          xung đột hay trợ lực trong vận mệnh.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="battu-daivan"]',
      title: "Bảng Đại Vận",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Đại vận chia cuộc đời thành các chu kỳ 10 năm. Mỗi vận mang một cặp Can Chi riêng, tương
          tác trực tiếp và làm biến đổi lá số gốc.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="battu-khaimo-mobile"]', '[data-tour="battu-khaimo"]'),
      title: "Khai mở luận giải",
      skipScroll: true,
      content: (
        <StepBody>
          {typeof window !== "undefined" && window.innerWidth < 768 ? (
            <>
              Bấm <strong>"Xem Luận Giải Chi Tiết"</strong> để chuyển sang khu vực Luận giải, sau đó
              bấm <strong>"Khai mở luận giải"</strong>.
            </>
          ) : (
            <>
              Bấm <strong>"Khai mở luận giải"</strong> để AI bắt đầu phân tích toàn diện về bản mệnh
              của bạn.
            </>
          )}
        </StepBody>
      ),
      data: {
        interaction: {
          event: "click",
          selector: "button",
          completion: "finish",
        },
      },
    },
  ],
  "battu-reading": [
    {
      target: "body",
      placement: "center",
      title: "Luận giải Bát Tự của bạn 🪷",
      content: (
        <StepBody>
          Bản luận giải chi tiết đã được khai mở! Hãy cùng đi qua các phần chính để hiểu sâu sắc hơn
          về bản mệnh.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="battu-gods"]'),
      title: "Dụng thần, Hỷ thần, Kỵ thần",
      skipScroll: true,
      content: (
        <StepBody>
          Xét Dụng thần, Hỷ thần để chọn phương vị, nghề nghiệp, màu sắc phù hợp giúp cải vận, và
          biết Kỵ thần cần tránh.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="battu-daicuc-anchor"]', '[data-tour="battu-daicuc"]'),
      title: "Đại cục bản mệnh",
      skipScroll: true,
      content: (
        <StepBody>
          Đọc luận giải tổng quan về cuộc đời, vận trình lên xuống của bạn tại phần Đại Cục Bản
          Mệnh.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="battu-sections"]'),
      title: "Các khía cạnh cuộc đời",
      skipScroll: true,
      content: (
        <StepBody>
          Bấm vào các thẻ này (Tính cách, Sự nghiệp, Tài lộc, Tình duyên, Gia đạo, Sức khỏe...) để
          xem luận giải chi tiết từng khía cạnh dưới dạng sớ lụa.
        </StepBody>
      ),
    },
    {
      target: pickVisible('[data-tour="battu-chat"]'),
      title: "Hỏi thầy thêm",
      skipScroll: true,
      content: (
        <StepBody>
          Nếu còn băn khoăn về bất kỳ khía cạnh nào, hãy bấm <strong>"Hỏi thầy thêm"</strong> để đàm
          đạo chi tiết cùng Cổ Nhân.
        </StepBody>
      ),
    },
  ],
  seduyen: [
    {
      target: "body",
      placement: "center",
      title: "Se duyên & Hợp tác 🌸",
      content: (
        <StepBody>
          Chào mừng bạn đến với tính năng kết nối hữu duyên! Dựa trên tương hợp lá số Tử Vi, hệ
          thống sẽ gợi ý người phù hợp với bạn.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-personal-info"]',
      title: "Sửa hồ sơ cá nhân 📝",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Bấm vào đây để cập nhật ảnh đại diện thực tế, số điện thoại hoặc mạng xã hội. Các thông
          tin này sẽ được bảo mật và chỉ chia sẻ khi hai bên đồng ý kết nối.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-purpose"]',
      title: "Mục đích kết nối 🎯",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Chọn <strong>Se duyên</strong> (tìm kiếm bạn đời/người yêu) hoặc{" "}
          <strong>Hợp tác làm ăn</strong> (tìm đối tác kinh doanh/đồng nghiệp). Bạn có thể chọn cả
          hai!
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-alias"]',
      title: "Biệt danh ẩn danh 👤",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Thiết lập biệt danh hiển thị công khai. Mọi người chỉ thấy biệt danh này khi quẹt thẻ,
          giúp bạn giữ sự riêng tư ban đầu.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-avatar-switch"]',
      title: "Hiển thị ảnh đại diện 🖼️",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Bật để cho phép mọi người xem avatar của bạn, hoặc tắt để ẩn danh hoàn toàn (chỉ hiển thị
          chữ cái đầu của biệt danh).
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-filters"]',
      title: "Bộ lọc đối phương 🎚️",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Chọn <strong>giới tính</strong> và <strong>độ tuổi</strong> mong muốn — tách riêng cho se
          duyên và làm ăn. Tiêu chí này giúp gợi ý đúng người hơn (ngoài khoảng vẫn hiện nhưng xếp
          sau).
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-location"]',
      title: "Vị trí của bạn 📍",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Thả ghim vị trí trên bản đồ để được ưu tiên gợi ý người ở gần. Người khác chỉ thấy{" "}
          <strong>khoảng cách ước lượng</strong>, không thấy địa chỉ chính xác.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-goals"]',
      title: "Mục tiêu sống / giá trị 🎯",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Chọn các mục tiêu sống (se duyên) hoặc tầm nhìn kinh doanh (làm ăn). Càng nhiều điểm chung
          với đối phương, điểm tương hợp càng cao.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-share-fields"]',
      title: "Thông tin chia sẻ 🤝",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Tích chọn các thông tin muốn tự động chia sẻ khi kết nối thành công. Bạn vẫn có thể tùy
          chỉnh lại riêng cho từng người sau này.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-submit"]',
      title: "Bật hồ sơ & Bắt đầu 🚀",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Bấm <strong>"Bật hồ sơ & bắt đầu"</strong> để hoàn tất và tham gia vào mạng lưới kết nối
          hữu duyên.
        </StepBody>
      ),
      data: {
        interaction: {
          event: "click",
          selector: "button",
          completion: "finish",
        },
      },
    },
  ],
  "seduyen-discover": [
    {
      target: "body",
      placement: "center",
      title: "Khám phá nhân duyên 💫",
      content: (
        <StepBody>
          Hồ sơ của bạn đã hoạt động! Hãy xem qua cách tìm kiếm và kết nối với người hữu duyên nhé.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-intent-toggle"]',
      title: "Chọn mục đích kết nối",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Chuyển đổi giữa <strong>"Se duyên"</strong> (đôi lứa) và <strong>"Hợp tác làm ăn"</strong>{" "}
          để xem các danh sách gợi ý tương ứng.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-deck"]',
      title: "Quẹt chọn nhân duyên",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Xem mức độ tương hợp (độ hợp số, lý do) và quẹt chọn: bấm <strong>X</strong> để bỏ qua,
          bấm <strong>Trái tim</strong> để bày tỏ quan tâm (thích).
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-search"]',
      title: "Tìm theo Username",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Nếu đã biết trước username của người khác trên Bói Đê, bạn có thể nhập vào đây để chủ động
          gửi yêu cầu kết nối trực tiếp.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-invite"]',
      title: "Mời bạn bè kết nối",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Sao chép đường link lời mời này gửi cho bạn bè hoặc người thương để họ kết nối trực tiếp
          và xem mức độ tương hợp Tử Vi của hai người.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="seduyen-tabs"]',
      title: "Quản lý kết nối & hồ sơ",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Tab <strong>Kết nối</strong> chứa danh sách những người đã tương hợp để bắt đầu xem luận
          giải chi tiết và trò chuyện. Tab <strong>Hồ sơ</strong> dùng để cập nhật lại cài đặt của
          bạn.
        </StepBody>
      ),
    },
  ],
  tonghop: [
    {
      target: "body",
      placement: "center",
      title: "Bói Đê 🔮",
      content: (
        <StepBody>
          Tính năng Chat Bói Đê cho phép bạn gom nhiều lá số Tử Vi, Bát Tự và gieo quẻ Kinh Dịch
          vào chung một phiên để luận giải tổng quát mối quan hệ hoặc nhiều mặt sự việc.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-subject-input"]',
      title: "Đặt tên phiên bói ✍️",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Nhập tên cho phiên bói (ví dụ: "Xem làm ăn năm 2026", "Tình duyên đôi lứa"...). Tên phiên
          giúp bạn dễ dàng quản lý và xem lại sau này.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-diviner-picker"]',
      title: "Chọn thầy luận giải 🧙",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Chọn vị thầy AI mà bạn muốn đàm đạo trong phiên bói này. Mỗi vị thầy có phong cách và góc
          nhìn luận giải đặc trưng.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-create-btn"]',
      title: "Tạo phiên bói 🚀",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Bấm <strong>"Tạo phiên"</strong> để khởi chạy phiên Bói Đê mới và bắt đầu gắn các lá
          số/quẻ dịch.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-list"]',
      title: "Danh sách phiên cũ 📂",
      skipScroll: true,
      placement: "top",
      content: (
        <StepBody>
          Nơi lưu trữ tất cả các phiên Bói Đê trước đây của bạn. Bạn có thể chọn bất kỳ phiên
          nào để xem lại lịch sử chat hoặc tiếp tục đàm đạo.
        </StepBody>
      ),
    },
  ],
  "tonghop-session": [
    {
      target: "body",
      placement: "center",
      title: "Phiên luận giải Bói Đê 🪷",
      content: (
        <StepBody>
          Đã vào phiên thành công! Hãy làm theo các bước sau để AI có đủ thông tin luận giải.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-charts"]',
      title: "Gắn lá số Tử Vi",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Bấm vào danh sách lá số Tử Vi đã lập của bạn để gắn vào phiên chat. Các lá số này sẽ làm
          bối cảnh để AI phân tích.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-bazi-charts"]',
      title: "Gắn lá số Bát Tự",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Bấm để gắn thêm lá số Bát Tự của bạn hoặc của đối phương để kết hợp phân tích tương tác
          can chi, ngũ hành.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-hexagrams"]',
      title: "Gieo quẻ Kinh Dịch",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Gieo thêm một hoặc nhiều quẻ dịch trong phiên để bổ sung thông điệp thời vận hiện tại vào
          câu hỏi của bạn.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="tonghop-chat"]',
      title: "Đặt câu hỏi luận giải",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Nhập câu hỏi Bói Đê ở đây để đàm đạo. AI sẽ kết hợp toàn bộ lá số Tử Vi, Bát Tự và quẻ
          dịch đã gắn để đưa ra câu trả lời toàn diện nhất.
        </StepBody>
      ),
    },
  ],
  "boide-hub": [
    {
      target: "body",
      placement: "center",
      title: "Bói Đê — xem theo chủ đề 🔮",
      content: (
        <StepBody>
          Mỗi ô là một chủ đề xem <strong>trọn gói</strong>: bạn nhập thông tin sinh, hệ thống tự
          lập lá số Tử Vi, Bát Tự và gieo quẻ Mai Hoa cho đúng câu hỏi, rồi thầy luận đan xen cả ba
          hệ trong một bài trả lời.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-groups"]',
      title: "Chọn việc cần xem 🗂️",
      placement: "bottom",
      content: (
        <StepBody>
          Các chủ đề được xếp theo nhóm: Xem nhanh, Tình cảm, Công danh, Gia đình, Nhà cửa, Khám
          phá bản thân. Giá hiện ngay trên từng ô — ô ghi <strong>"từ ..."</strong> nghĩa là có thể
          thêm người và cộng phụ phí.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-tile"]',
      title: "Mỗi ô một mức giá 💰",
      skipScroll: true,
      placement: "bottom",
      content: (
        <StepBody>
          Ô có nhãn số người (ví dụ "1–2 người", "2–6 người") là chủ đề xem được nhiều người — như
          tình duyên thêm người ấy để chấm tương hợp, cưới hỏi xét tuổi cả hai họ. Trả phí một lần,
          sau lời luận hỏi thêm thoải mái (1đ/token).
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-chart-tools"]',
      title: "Lập lá số miễn phí 🌟",
      placement: "top",
      content: (
        <StepBody>
          Muốn tự xem lá số Tử Vi, Bát Tự trước? Lập miễn phí tại đây — không cần trả phí chủ đề.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-free"]',
      title: "Đàm đạo tự do 💬",
      placement: "top",
      content: (
        <StepBody>
          Không muốn theo chủ đề? Ô này cho bạn tự ghép nhiều lá số, gieo nhiều quẻ và hỏi tự do —
          tính phí theo tin nhắn.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-guide"]',
      title: "Hướng dẫn chi tiết 📖",
      placement: "top",
      content: (
        <StepBody>
          Mọi thắc mắc về giá, số người, các dạng kết quả... đều có ở mục này. Kết quả đã xem lưu
          tại trang <strong>Lịch sử</strong> — mở lại là chat tiếp được với thầy.
        </StepBody>
      ),
    },
  ],
  "boide-topic": [
    {
      target: '[data-tour="boide-person-form"]',
      title: "Nhập thông tin sinh ✍️",
      placement: "bottom",
      content: (
        <StepBody>
          Điền họ tên, ngày giờ sinh (dương hoặc âm lịch) của người cần xem. Không nhớ giờ sinh thì
          chọn <strong>"Không rõ"</strong>. Chủ đề cho phép sẽ có nút thêm người ngay bên dưới (cộng
          phụ phí).
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-question"]',
      title: "Câu hỏi & chọn thầy 🧙",
      placement: "top",
      content: (
        <StepBody>
          Ghi điều bạn đang băn khoăn để thầy luận sát hơn (mục có dấu <strong>*</strong> là bắt
          buộc), và chọn vị thầy hợp cơ duyên — bài luận sẽ mang đúng giọng thầy bạn chọn.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boide-start"]',
      title: "Bắt đầu xem 🚀",
      placement: "top",
      content: (
        <StepBody>
          Giá trọn gói hiện ngay trên nút (đã gồm phụ phí người thêm nếu có). Bấm và chờ 30–90 giây
          — nếu luận lỗi, hệ thống tự hoàn tiền. Sau lời luận, bạn hỏi thêm thoải mái với phí
          1đ/token.
        </StepBody>
      ),
    },
  ],
  "boi-live": [
    {
      target: "body",
      placement: "center",
      title: "Đàm đạo trực tiếp Bói Live 🎙️",
      content: (
        <StepBody>
          Chào mừng bạn đến với Bói Live! Đây là tính năng đàm đạo trực tiếp bằng giọng nói với thầy
          bói AI. Thầy có thể lắng nghe bạn tâm sự, tự động gieo quẻ Dịch hoặc lập lá số Tử Vi/Bát
          Tự ngay trong cuộc gọi.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-picker"]',
      title: "Chọn vị thầy hữu duyên 🪷",
      placement: "bottom",
      content: (
        <StepBody>
          Mỗi vị thầy bói AI có một giọng đọc và phong cách luận giải riêng biệt. Hãy trượt và chọn
          vị thầy phù hợp nhất với câu chuyện của bạn.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-settings"]',
      title: "Cài đặt & Tốc độ nói ⚙️",
      placement: "bottom",
      content: (
        <StepBody>
          Bấm vào nút cài đặt này để điều chỉnh tốc độ nói của thầy (Chậm, Vừa, Nhanh) hoặc bật phụ
          đề hiển thị hội thoại.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-pricing"]',
      title: "Chi phí & Số dư tài khoản 🪙",
      placement: "top",
      content: (
        <StepBody>
          Chi phí được tính theo giây đàm thoại thực tế. Bạn có thể theo dõi đơn giá mỗi phút và số
          dư khả dụng ngay tại đây.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-start-cta"]',
      title: "Bắt đầu đàm đạo 📞",
      placement: "top",
      content: (
        <StepBody>
          Khi đã sẵn sàng, hãy bấm nút này để cấp quyền micro cho trình duyệt và kết nối trực tiếp
          đến thầy.
        </StepBody>
      ),
    },
  ],
  "boi-live-session": [
    {
      target: "body",
      placement: "center",
      title: "Đã kết nối cuộc gọi thoại! 🗣️",
      content: (
        <StepBody>
          Bạn đã kết nối thành công với thầy. Hãy xem qua các công cụ hỗ trợ tương tác trong lúc nói
          chuyện nhé.
        </StepBody>
      ),
    },
    {
      target: pickVisible(
        '[data-tour="boi-live-avatar-desktop"]',
        '[data-tour="boi-live-avatar-mobile"]',
      ),
      title: "Avatar & Trạng thái của thầy 🧘",
      placement: "bottom",
      content: (
        <StepBody>
          Nơi hiển thị hình ảnh của thầy. Khi thầy đang nói hoặc đang thực hiện các thao tác gieo
          quẻ, lập lá số thì sóng âm và thông tin trạng thái tương ứng sẽ hiển thị ở đây.
        </StepBody>
      ),
    },
    {
      target: pickVisible(
        '[data-tour="boi-live-controls-desktop"]',
        '[data-tour="boi-live-controls-mobile"]',
      ),
      title: "Bảng điều khiển cuộc gọi 🎛️",
      placement: "top",
      content: (
        <StepBody>
          Tại đây bạn có thể bật/tắt micro của mình, ẩn/hiện hộp văn bản ghi âm cuộc trò chuyện,
          hoặc kết thúc phiên bói live.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-results"]',
      title: "Hồ sơ & Kết quả đã lập 🔮",
      placement: "top",
      content: (
        <StepBody>
          Nơi lưu giữ các kết quả: bấm vào đây để xem lại danh sách các lá số Tử Vi, Bát Tự hoặc quẻ
          Kinh Dịch mà thầy đã lập cho bạn ngay trong cuộc gọi này.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-transcript"]',
      title: "Hộp thoại & Kết quả lập số 📜",
      placement: "top",
      content: (
        <StepBody>
          Toàn bộ nội dung trò chuyện (dạng văn bản) và các lá số Tử Vi, Bát Tự hoặc quẻ Kinh Dịch
          gieo được trong lúc trò chuyện sẽ hiển thị đầy đủ ở khu vực này để bạn tiện theo dõi.
        </StepBody>
      ),
    },
    {
      target: '[data-tour="boi-live-text-input"]',
      title: "Nhập văn bản dự phòng ⌨️",
      placement: "top",
      content: (
        <StepBody>
          Nếu bạn ở nơi ồn ào hoặc không tiện nói chuyện trực tiếp, bạn hoàn toàn có thể gõ câu hỏi
          vào ô này để gửi cho thầy.
        </StepBody>
      ),
    },
  ],
};
